/**
 * M7 S13: Mission 3's people logic (spec §6): the hideout hold-back (W5), the trust test (T5 logic half), Okafor's
 * risk (D-315 #5; T18 Okafor half), his custody, rescue and loss.
 *
 * - **Hold-back:** `okafor-hideout` is never a generic laneRepair or relay job before story tier 3
 *   (`hideoutJobsHeldBack`, read by `src/contracts/generate.ts`), so nobody starts Okafor's risk by chance.
 * - **The risk** starts only when Mission 3's relay step is done **and** the mission is active (or the hideout was
 *   docked). It is two online-day countdowns (`countdowns.ts`, never during a catch-up): the warning
 *   (`okaforWarning`, 3–5 online days: a Wire item and Kell's line), then custody (`okaforCustody`, 5–8 more) if he
 *   is still at the hideout. Custody is never a death: a prisoner transport (mission-owned traffic) carries him, and
 *   the player can rescue him from it or leave him. Unrescued for `lostOnlineDays`, he is lost for good and his lab
 *   notes remain (a weaker story item). Day counts come from the career seed (deterministic per career).
 * - **The trust test** (`proveTrust`) accepts a meter reading of a Tethys operation taken within the window, the
 *   player's tetherstone records, or the Watch history. With none and no Tethys rig in the Reach it asks for one rig
 *   as mission-owned traffic. The step's target is a reply the comms UI never shows, so only this function completes it.
 *
 * Everything is driven by story events (`okaforAfterEvent`, which the story link calls after `stepMission`) and the
 * countdown handlers (`okaforHandlers`, passed to `tickOnlineDay`). Pure: Career in, Career out.
 */
import { z } from 'zod'
import type { Career } from '../career/types'
import m3m4Json from '../data/story/m3m4.json'
import { seededUnit } from '../space/hash'
import { FACTION_IDS } from '../strategic/types'
import { cancelCountdown, countdownOf, scheduleCountdown, type CountdownHandler } from './countdowns'
import { grantItem } from './items'
import { tetherTotalsOf } from './logging'
import { meterProofsOf } from './meter'
import { missionOf, stepMission, type MissionContext } from './missions'
import { storyOf, withStory } from './state'
import {
  WIRE_OUTLETS,
  type CountdownKind,
  type OkaforState,
  type Speaker,
  type StoryCommand,
  type StoryEffect,
  type StoryEvent,
  type StoryResult,
} from './types'

// ---------------------------------------------------------------------------------------------
// m3m4.json
// ---------------------------------------------------------------------------------------------

const id = z.string().min(1)
const days = z.number().int().nonnegative()
const dayRange = z.tuple([days, days]).refine(([a, b]) => a <= b, 'range [min, max] with min ≤ max')
const wireSchema = z.strictObject({ headline: id, severity: z.number().int().min(0).max(3), source: z.enum(WIRE_OUTLETS) })

export const m3m4FileSchema = z.strictObject({
  comment: z.string(),
  okafor: z.strictObject({
    kellMinHoldfast: z.number().min(-100).max(100),
    hideout: z.strictObject({
      anchor: id,
      genericJobsFromTier: z.number().int().min(0).max(5),
      relayStep: id,
      dockStep: id,
      trustStep: id,
      reportStep: id,
    }),
    risk: z.strictObject({ warningOnlineDays: dayRange, custodyOnlineDays: dayRange, lostOnlineDays: days }),
    trust: z.strictObject({
      readingWindowDays: days,
      minTetherSales: z.number().int().positive(),
      watchLogs: z.array(id),
      node: id,
      askReply: id,
      proofReply: id,
      rigKey: id,
      rigSystem: id,
    }),
    rescue: z.strictObject({ transportKey: id, transportSystem: id, leaveNode: id, leaveReply: id }),
    comms: z.strictObject({
      proven: id,
      noProof: id,
      noRig: id,
      held: id,
      warning: id,
      custody: id,
      rescued: id,
      lost: id,
      bulletinOffer: id,
    }),
    wire: z.strictObject({ warning: wireSchema, custody: wireSchema }),
  }),
  hale: z.strictObject({
    bountyFaction: z.enum(FACTION_IDS),
    holdfastClaimId: id,
    enmityBounty: z.number().nonnegative(),
    dealFork: id,
    takeChoice: id,
    comms: z.strictObject({ voided: id }),
    wire: z.strictObject({ audit: wireSchema, voided: wireSchema }),
  }),
})
export type M3M4Config = z.output<typeof m3m4FileSchema>
export type OkaforConfig = M3M4Config['okafor']
export type WirePost = z.output<typeof wireSchema>

/** Parses an `m3m4.json` (tests pass their own); throws with the path on a bad file. */
export function loadM3M4Config(raw: unknown): M3M4Config {
  const result = m3m4FileSchema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`story data: m3m4.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

let cached: M3M4Config | undefined

/** The shipped `m3m4.json`, parsed once on first use. */
export function m3m4Config(): M3M4Config {
  cached ??= loadM3M4Config(m3m4Json)
  return cached
}

/** The worker's Wire command for a configured post. */
export function wireCommand(post: WirePost): StoryCommand {
  return { type: 'postWire', headline: post.headline, severity: post.severity, source: post.source }
}

// ---------------------------------------------------------------------------------------------
// The hideout hold-back (W5)
// ---------------------------------------------------------------------------------------------

/** True when a generic contract job in a system with this anchor is held back at this story tier (spec §6). */
export function hideoutJobsHeldBack(anchor: string | null, storyTier: number, cfg: OkaforConfig = m3m4Config().okafor): boolean {
  return anchor === cfg.hideout.anchor && storyTier < cfg.hideout.genericJobsFromTier
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

export const OKAFOR_COUNTDOWN = { warning: 'okafor:warning', custody: 'okafor:custody', lost: 'okafor:lost' } as const

/** Okafor is (as far as anyone knows) still at the hideout: not met yet, or met there. */
export function okaforAtHideout(state: OkaforState | undefined): boolean {
  return state === undefined || state === 'hideout'
}

/** True when Mission 3's `stepId` is done (a later step is current, its fork is pending, or the mission is complete). */
export function m3StepDone(career: Career, stepId: string, ctx: MissionContext): boolean {
  const m = missionOf(career, 'm3')
  if (m === undefined) return false
  if (m.state === 'complete') return true
  const steps = ctx.missions.m3.steps
  const at = steps.findIndex((s) => s.id === stepId)
  const now = steps.findIndex((s) => s.id === m.step)
  if (at < 0 || now < 0) return false
  return now > at || (now === at && m.awaitingFork !== undefined)
}

/** The M3 giver (spec §6): Kell at Holdfast ≥ the threshold, otherwise the Mutual Bulletin lead. */
export function m3OfferContact(career: Career, ctx: MissionContext, cfg: OkaforConfig = m3m4Config().okafor): { speaker: Speaker; node: string } {
  if (career.standings.holdfast >= cfg.kellMinHoldfast) {
    const offer = ctx.missions.m3.offer
    return { speaker: offer.contact, node: offer.comms ?? cfg.comms.bulletinOffer }
  }
  return { speaker: 'mutualArchivist', node: cfg.comms.bulletinOffer }
}

const SALT_WARNING = 0x0ca7_0001
const SALT_CUSTODY = 0x0ca7_0002

/** A whole number of online days in `[min, max]`, drawn from the career seed and the online day (deterministic). */
export function pickOnlineDays(career: Career, range: readonly [number, number], salt: number): number {
  const [min, max] = range
  const u = seededUnit(career.seed >>> 0, storyOf(career).onlineDays, salt)
  return min + Math.min(max - min, Math.floor(u * (max - min + 1)))
}

// ---------------------------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------------------------

function same(career: Career): StoryResult<Career> {
  return { career, commands: [], effects: [] }
}

function comms(speaker: Speaker, nodeId: string): StoryEffect {
  return { type: 'comms', speaker, nodeId }
}

function setOkafor(career: Career, state: OkaforState): Career {
  return withStory(career, (s) => ({ ...s, okaforState: state }))
}

// ---------------------------------------------------------------------------------------------
// The risk
// ---------------------------------------------------------------------------------------------

/** True once the risk has begun (a countdown is running, he was warned, or he has left the hideout either way). */
export function okaforRiskStarted(career: Career): boolean {
  const story = storyOf(career)
  if (story.flags.okafor_warned === true) return true
  return Object.values(OKAFOR_COUNTDOWN).some((cid) => countdownOf(career, cid) !== undefined)
}

/** Why the risk can't start now, or null (W5: never without Mission 3 active or a hideout dock, and the relay repaired). */
export function okaforRiskBlock(
  career: Career,
  ctx: MissionContext,
  cfg: OkaforConfig = m3m4Config().okafor,
): 'started' | 'notAtHideout' | 'noMission' | 'relayDown' | 'notActive' | null {
  if (okaforRiskStarted(career)) return 'started'
  if (!okaforAtHideout(storyOf(career).okaforState)) return 'notAtHideout'
  const m = missionOf(career, 'm3')
  if (m === undefined || m.state === 'complete') return 'noMission'
  if (!m3StepDone(career, cfg.hideout.relayStep, ctx)) return 'relayDown'
  const docked = m3StepDone(career, cfg.hideout.dockStep, ctx)
  if (m.state !== 'active' && !docked) return 'notActive'
  return null
}

/** Starts the warning countdown when the conditions hold (idempotent: a started risk is never restarted). */
export function maybeStartOkaforRisk(career: Career, ctx: MissionContext, cfg: OkaforConfig = m3m4Config().okafor): Career {
  if (okaforRiskBlock(career, ctx, cfg) !== null) return career
  return scheduleCountdown(career, {
    id: OKAFOR_COUNTDOWN.warning,
    kind: 'okaforWarning',
    inOnlineDays: pickOnlineDays(career, cfg.risk.warningOnlineDays, SALT_WARNING),
  })
}

function completeM3(career: Career, effects: StoryEffect[]): Career {
  const m = missionOf(career, 'm3')
  if (m === undefined || m.state === 'complete') return career
  effects.push({ type: 'missionComplete', missionId: 'm3' }, { type: 'autosave', reason: 'mission:m3:complete' })
  return withStory(career, (s) => ({
    ...s,
    missions: s.missions.map((x) => {
      if (x.id !== 'm3') return x
      const done = { ...x, state: 'complete' as const }
      delete done.awaitingFork
      return done
    }),
  }))
}

/** With Okafor lost, the steps that need him can't run: Mission 3 closes there (his lab notes are what's left). */
function closeM3IfStuck(career: Career, cfg: OkaforConfig, effects: StoryEffect[]): Career {
  if (storyOf(career).okaforState !== 'lost') return career
  const m = missionOf(career, 'm3')
  if (m === undefined || m.state === 'complete') return career
  const needsHim = [cfg.hideout.dockStep, cfg.hideout.trustStep, cfg.hideout.reportStep]
  if (!needsHim.includes(m.step)) return career
  return completeM3(career, effects)
}

/** He is taken (never killed): the transport is mission-owned traffic; the loss countdown starts. */
function takeIntoCustody(career: Career, cfg: OkaforConfig): StoryResult<Career> {
  let next = setOkafor(career, 'held')
  next = scheduleCountdown(next, { id: OKAFOR_COUNTDOWN.lost, kind: 'okaforCustody', inOnlineDays: cfg.risk.lostOnlineDays, ref: 'lost' })
  return {
    career: next,
    commands: [
      { type: 'spawnStoryShip', key: cfg.rescue.transportKey, role: 'prisonerTransport', systemId: cfg.rescue.transportSystem, missionId: 'm3' },
      wireCommand(cfg.wire.custody),
    ],
    effects: [comms('kell', cfg.comms.custody), { type: 'autosave', reason: 'okafor:held' }],
  }
}

/** Lost for good (unrescued, or the player left him): his lab notes remain; M3 closes if it needs him next. */
export function loseOkafor(career: Career, cfg: OkaforConfig = m3m4Config().okafor): StoryResult<Career> {
  const state = storyOf(career).okaforState
  if (state !== 'held') return same(career)
  let next = setOkafor(career, 'lost')
  for (const cid of Object.values(OKAFOR_COUNTDOWN)) next = cancelCountdown(next, cid)
  const notes = grantItem(next, 'lab_notes')
  next = notes.career
  const effects: StoryEffect[] = [...notes.effects, comms('kell', cfg.comms.lost), { type: 'autosave', reason: 'okafor:lost' }]
  next = closeM3IfStuck(next, cfg, effects)
  return { career: next, commands: [], effects }
}

/** The transport is stopped and he is brought aboard. */
export function rescueOkafor(career: Career, cfg: OkaforConfig = m3m4Config().okafor): StoryResult<Career> {
  if (storyOf(career).okaforState !== 'held') return same(career)
  const next = cancelCountdown(setOkafor(career, 'aboard'), OKAFOR_COUNTDOWN.lost)
  return { career: next, commands: [], effects: [comms('okafor', cfg.comms.rescued), { type: 'autosave', reason: 'okafor:rescued' }] }
}

/** The countdown handlers for `tickOnlineDay` (online days only; nothing here runs during a catch-up). */
export function okaforHandlers(cfg: OkaforConfig = m3m4Config().okafor): Partial<Record<CountdownKind, CountdownHandler>> {
  return {
    okaforWarning: (career) => {
      if (!okaforAtHideout(storyOf(career).okaforState)) return same(career)
      let next = withStory(career, (s) => ({ ...s, flags: { ...s.flags, okafor_warned: true } }))
      next = scheduleCountdown(next, {
        id: OKAFOR_COUNTDOWN.custody,
        kind: 'okaforCustody',
        inOnlineDays: pickOnlineDays(next, cfg.risk.custodyOnlineDays, SALT_CUSTODY),
        ref: 'custody',
      })
      return { career: next, commands: [wireCommand(cfg.wire.warning)], effects: [comms('kell', cfg.comms.warning)] }
    },
    okaforCustody: (career, fired) => {
      if (fired.ref === 'lost') return loseOkafor(career, cfg)
      if (!okaforAtHideout(storyOf(career).okaforState)) return same(career)
      return takeIntoCustody(career, cfg)
    },
  }
}

// ---------------------------------------------------------------------------------------------
// The trust test (T5)
// ---------------------------------------------------------------------------------------------

export const TRUST_PROOFS = ['reading', 'records', 'watch'] as const
export type TrustProof = (typeof TRUST_PROOFS)[number]

/** The first accepted proof the player can show on faction day `day`, or null. */
export function trustProof(career: Career, day: number, cfg: OkaforConfig = m3m4Config().okafor): TrustProof | null {
  const window = cfg.trust.readingWindowDays
  if (meterProofsOf(career).some((p) => p.day <= day && day - p.day <= window)) return 'reading'
  if (tetherTotalsOf(career).sales >= cfg.trust.minTetherSales) return 'records'
  const story = storyOf(career)
  if (cfg.trust.watchLogs.some((l) => story.readLogs.includes(l) || story.unreadLogs.includes(l))) return 'watch'
  return null
}

export interface OkaforInput {
  /** The worker's faction day. */
  day: number
  /** Tethys extraction rigs in the Reach (AI and story ones); 0 asks for a mission rig. */
  tethysRigs: number
}

export interface TrustResult extends StoryResult<Career> {
  proof: TrustProof | null
}

/**
 * The player shows Okafor what they have (the trust node's ask reply). An accepted proof completes the trust step;
 * none opens his refusal, and with no Tethys rig anywhere a mission rig is requested (keyed, so it spawns once).
 */
export function proveTrust(career: Career, input: OkaforInput, ctx: MissionContext, cfg: OkaforConfig = m3m4Config().okafor): TrustResult {
  const m = missionOf(career, 'm3')
  if (m === undefined || m.state === 'complete' || m.step !== cfg.hideout.trustStep || m.awaitingFork !== undefined) {
    return { ...same(career), proof: null }
  }
  const state = storyOf(career).okaforState
  if (state === 'held' || state === 'lost') return { career, commands: [], effects: [comms('kell', cfg.comms.held)], proof: null }
  const proof = trustProof(career, input.day, cfg)
  if (proof !== null) {
    const r = stepMission(career, { type: 'commsReply', nodeId: cfg.trust.node, replyId: cfg.trust.proofReply }, ctx)
    return { ...r, proof }
  }
  if (input.tethysRigs > 0) return { career, commands: [], effects: [comms('okafor', cfg.comms.noProof)], proof: null }
  return {
    career,
    commands: [{ type: 'spawnStoryShip', key: cfg.trust.rigKey, role: 'missionRig', systemId: cfg.trust.rigSystem, missionId: 'm3' }],
    effects: [comms('okafor', cfg.comms.noRig)],
    proof: null,
  }
}

// ---------------------------------------------------------------------------------------------
// The event hook
// ---------------------------------------------------------------------------------------------

function merge(a: StoryResult<Career>, b: StoryResult<Career>): StoryResult<Career> {
  return { career: b.career, commands: [...a.commands, ...b.commands], effects: [...a.effects, ...b.effects] }
}

/**
 * The story link calls this after `stepMission` for every story event: the trust ask, leaving him, the rescue, his
 * state at the hideout dock, the risk's start, and closing Mission 3 when he is lost and it needs him next.
 */
export function okaforAfterEvent(
  career: Career,
  event: StoryEvent,
  input: OkaforInput,
  ctx: MissionContext,
  cfg: OkaforConfig = m3m4Config().okafor,
): StoryResult<Career> {
  let out = same(career)
  if (event.type === 'commsReply' && event.nodeId === cfg.trust.node && event.replyId === cfg.trust.askReply) {
    out = merge(out, proveTrust(out.career, input, ctx, cfg))
  } else if (event.type === 'commsReply' && event.nodeId === cfg.rescue.leaveNode && event.replyId === cfg.rescue.leaveReply) {
    out = merge(out, loseOkafor(out.career, cfg))
  } else if (event.type === 'killed' && event.storyShip === cfg.rescue.transportKey) {
    out = merge(out, rescueOkafor(out.career, cfg))
  }
  if (storyOf(out.career).okaforState === undefined && m3StepDone(out.career, cfg.hideout.dockStep, ctx)) {
    out = { ...out, career: setOkafor(out.career, 'hideout') }
  }
  out = { ...out, career: maybeStartOkaforRisk(out.career, ctx, cfg) }
  const effects: StoryEffect[] = []
  const closed = closeM3IfStuck(out.career, cfg, effects)
  return { career: closed, commands: out.commands, effects: [...out.effects, ...effects] }
}
